import type { MessageSequenceStatus, MessageSort } from '@api'
import { z } from 'zod'

import type { FilterOptionValue } from '@/modules/expiring-fans/components/tables/fans/expiring-fans-table.tsx'
import type {
  ExpiringFansMessage,
  ExpiringFansMessageRequestBody,
} from '@/modules/expiring-fans/types.ts'
import { apiClient, apiClientV3 } from '@/utils/axios'

const LinkSchema = z.object({
  id: z.number().nonnegative().int(),
  url: z.string().nullish(),
  name: z.string().nullish(),
})

export const ExpiringFanSchema = z.object({
  fan: z.object({
    id: z.number().nonnegative().int(),
    name: z.string(),
    username: z.string().nullish(),
    displayName: z.string().nullish(),
    avatar: z.string().nullish(),
    link: LinkSchema.nullish(),
    subscribedOnData: z
      .object({
        totalSumm: z.number().nullish(),
        expiredAt: z.string().nullish(),
      })
      .nullish(),
  }),
  status: z
    .enum(['AUTO_MESSAGED', 'MANUALLY_MESSAGED', 'QUEUED', 'NOT_MESSAGED'])
    .nullish(),
})

export type ExpiringFan = z.infer<typeof ExpiringFanSchema>

export interface Pagination {
  size: number
  page: number
}

export type ListExpiringFansResponseType = z.infer<
  typeof ListExpiringFansResponseSchema
>

export function PageResponseSchema<T>(schema: z.ZodType<T>) {
  return z.object({
    content: z.array(schema),
    totalPages: z.number(),
    totalElements: z.number(),
    size: z.number(),
    numberOfElements: z.number(),
    last: z.boolean(),
    first: z.boolean(),
    empty: z.boolean(),
  })
}

const ListExpiringFansResponseSchema = PageResponseSchema(ExpiringFanSchema)

const list = (
  creatorId: number,
  searchQuery: string,
  filter: FilterOptionValue | string,
  sort: string,
  sortDirection: 'ASC' | 'DESC',
  pagination?: Pagination,
) => {
  return apiClient
    .get(`/fans/expiring-fans/${creatorId}`, {
      params: { ...pagination, searchQuery, filter, sort, sortDirection },
    })
    .then((response) => ListExpiringFansResponseSchema.parse(response.data))
}

export interface ExpiringFanSettingsForm {
  enabled: boolean
  hoursBeforeExpiry: number
  minHoursSinceLastMessage: number
  maxSpend: number | null
}

const markAsMessaged = (
  creatorId: number,
  fanId: number,
  messaged: boolean,
) => {
  return apiClient.post(`/fans/expiring-fans/${creatorId}/messaged`, {
    fanId,
    messaged,
  })
}

function getListMessages(creatorId: number, sortOption: MessageSort) {
  return apiClientV3
    .get<ExpiringFansMessage[]>(`/auto-message-expiring-fans/${creatorId}`, {
      params: {
        sortBy: sortOption,
      },
    })
    .then((response) => {
      return response.data
    })
}

function getMessageById(creatorId: number, messageId: string) {
  return apiClientV3
    .get<ExpiringFansMessage>(
      `/auto-message-expiring-fans/${creatorId}/${messageId}`,
    )
    .then((response) => {
      return response.data
    })
}

function updateMessage(
  creatorId: number,
  message: ExpiringFansMessageRequestBody,
) {
  return apiClientV3.put(
    `/auto-message-expiring-fans/${creatorId}/${message.id}`,
    message,
  )
}

function createMessage(
  creatorId: number,
  message: ExpiringFansMessageRequestBody,
) {
  return apiClientV3.post<ExpiringFansMessage>(
    `/auto-message-expiring-fans/${creatorId}`,
    message,
  )
}

function deleteMessage(creatorId: number, messageId: number) {
  return apiClientV3.delete(
    `/auto-message-expiring-fans/${creatorId}/${messageId}`,
  )
}

const getSettings = (creatorId: number) => {
  return apiClientV3
    .get<ExpiringFanSettingsForm>(
      `/auto-message-expiring-fans/${creatorId}/settings`,
    )
    .then((response) => response.data)
}

const updateSettings = (
  creatorId: number,
  settings: ExpiringFanSettingsForm,
) => {
  return apiClientV3.put(
    `/auto-message-expiring-fans/${creatorId}/settings`,
    settings,
  )
}

export function updateExpiringFansMessageToggle(
  creatorId: number,
  messageId: string,
  value: MessageSequenceStatus,
) {
  return apiClientV3.put(
    `/auto-message-expiring-fans/${creatorId}/${messageId}/toggle-message`,
    value,
  )
}

export const expiringFanService = {
  list,
  markAsMessaged,
  getListMessages,
  getMessageById,
  createMessage,
  deleteMessage,
  updateMessage,
  getSettings,
  updateSettings,
  updateExpiringFansMessageToggle,
}
